import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useDimensions } from '@libs/hooks';
import { screenSizes } from '@libs/theme';
import { useCallback, useState } from 'react';
import { useTheme } from 'styled-components';
import { CheckboxType } from '../../checkbox/Checkbox';
import { TransparentButton } from '../../transparent-button/TransparentButton';
import Typography from '../../typography/Typography';
import { Separator } from '../select-modal/styles';
import { CustomButton, CustomClearButton, CustomRoundedCheckbox, CustomSelectModal, CustomTypography, FooterButtonWrapper, MultiSelectContainer, MultiSelectValueContainer, OptionHeaderContainer, SelectValueContainer, } from './styles';
const MultiSelect = function ({ options = [], selectedValues, onChange, renderValue, renderOption, onFiltersSubmit, clearButtonText, optionSize = 5.6, placeholder, textAfterSelection, onHeaderButtonClick, emptyValueText, renderOptionHeader, renderOptionFooter, onClose, filterButtonText, onFilterClear, className, ...restProps }) {
    const { colors } = useTheme();
    const { width } = useDimensions();
    const [clearOptionsState, setClearOptionsState] = useState(false);
    const filterButtonFormattedText = selectedValues.length
        ? filterButtonText + ` (${selectedValues.length})`
        : filterButtonText;
    const defaultRenderValue = useCallback((val, values) => {
        if (renderValue) {
            return renderValue(values);
        }
        if (width <= screenSizes.tablet) {
            const isSelected = values?.length !== 0;
            const valueFontSize = isSelected ? 'primaryBold' : 'primaryMedium';
            const textColor = isSelected
                ? colors.text.interactive
                : colors.text.default;
            return (_jsx(SelectValueContainer, { children: _jsxs(Typography, { type: "bodyButton", fontFamily: "primaryBold", color: textColor, children: [textAfterSelection && values?.length
                            ? textAfterSelection
                            : placeholder, _jsx(Typography, { type: "bodyButton", fontFamily: valueFontSize, color: textColor, children: ' • ' + (values?.length ? values.length : emptyValueText) })] }) }));
        }
        const placeholderColor = selectedValues?.length !== 0 || emptyValueText
            ? colors.text.secondary
            : colors.text.default;
        const placeholderType = selectedValues?.length !== 0 || emptyValueText
            ? 'caption'
            : 'subHeadline';
        return (_jsxs(MultiSelectValueContainer, { children: [(!!placeholder || !!textAfterSelection) && (_jsx(Typography, { color: placeholderColor, type: placeholderType, className: "title", children: emptyValueText && textAfterSelection
                        ? textAfterSelection
                        : placeholder })), _jsx(CustomTypography, { type: "subHeadline", fontFamily: "primaryMedium", className: "value-class", children: values?.length && values.length !== options.length
                        ? values.map(({ label }) => label).join(', ')
                        : emptyValueText })] }));
    }, [
        renderValue,
        width,
        selectedValues,
        emptyValueText,
        colors,
        placeholder,
        textAfterSelection,
        options,
    ]);
    const defaultRenderOptionHeader = useCallback((onClose) => {
        const handleClick = () => {
            onClose();
            onFilterClear?.();
        };
        return (_jsxs(_Fragment, { children: [_jsxs(OptionHeaderContainer, { children: [_jsx(Typography, { type: "subHeadline", fontFamily: "primaryBold", children: placeholder }), selectedValues.length !== 0 && (_jsx(TransparentButton, { onClick: handleClick, children: _jsx(Typography, { type: "bodyButton", fontFamily: "primaryMedium", color: colors.interactive.brandColor, children: clearButtonText }) }))] }), _jsx(Separator, {})] }));
    }, [
        clearButtonText,
        colors.interactive.brandColor,
        onFilterClear,
        placeholder,
        selectedValues,
    ]);
    const customRenderOption = useCallback((opt, index) => {
        const isSelected = selectedValues?.find((item) => item === opt.value);
        const isLast = index === (options?.length || 0) - 1;
        const handleChange = () => {
            const isAlreadySelected = selectedValues.includes(opt.value);
            if (isAlreadySelected) {
                const filteredValue = selectedValues.filter((item) => item !== opt.value);
                onChange?.(filteredValue);
            }
            else {
                onChange?.((prev) => [...prev, opt.value]);
            }
        };
        return (_jsxs("li", { children: [_jsx(CustomRoundedCheckbox, { checkboxType: CheckboxType.PRIMARY, optionSize: optionSize || (width <= screenSizes.tablet ? 6.8 : 5.8), title: opt.label, disabled: opt.disabled, value: opt.value, onClick: handleChange, isSelected: !!isSelected, description: opt?.description, icon: opt.icon }, index), _jsx(Separator, { disabled: opt.disabled, isLast: isLast })] }, opt.value));
    }, [selectedValues, options?.length, optionSize, width, onChange]);
    const defaultRenderOptionFooter = useCallback((onClose) => {
        const handleClick = () => {
            setClearOptionsState(false);
            onClose();
            onFiltersSubmit?.();
        };
        const handleClearButton = () => {
            setClearOptionsState(true);
            onFilterClear?.();
        };
        return (_jsx(_Fragment, { children: _jsxs(FooterButtonWrapper, { children: [clearButtonText && renderOptionHeader && (_jsx(CustomClearButton, { title: clearButtonText, onClick: handleClearButton, size: "md", buttonType: "secondary", disabled: !selectedValues.length })), _jsx(CustomButton, { title: filterButtonFormattedText, onClick: handleClick, size: "md", disabled: !selectedValues.length && !clearOptionsState })] }) }));
    }, [
        clearOptionsState,
        clearButtonText,
        renderOptionHeader,
        selectedValues.length,
        filterButtonFormattedText,
        onFiltersSubmit,
        onFilterClear,
    ]);
    return (_jsx(MultiSelectContainer, { isSelected: selectedValues.length !== 0, className: className, children: _jsx(CustomSelectModal, { options: options, placeholder: placeholder, renderValue: defaultRenderValue, onHeaderButtonClick: selectedValues.length !== 0 ? onHeaderButtonClick : undefined, renderOption: renderOption || customRenderOption, renderOptionHeader: renderOptionHeader || defaultRenderOptionHeader, renderOptionFooter: renderOptionFooter || defaultRenderOptionFooter, selectedValues: selectedValues, shouldCloseOnSelection: false, onClose: onClose, textAfterSelection: textAfterSelection, className: "select-modal", ...restProps }) }));
};
export default MultiSelect;
//# sourceMappingURL=MultiSelect.js.map